import { useFlyOutProductsBasedOnSelectedCloud } from '../../utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { FieldArray, FormikProps } from 'formik'
import {
  ButtonV2,
  LabraToolTipV2,
  NoteV2,
  PlanType,
  SelectV4,
  TextInputV4,
} from '@labrav/react-components'
import { get, isNil, isUndefined } from 'lodash'
import { Dimension, DimensionType } from '../../../types/privateOffer'
import { Box, SelectChangeEvent, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { Fragment, useCallback, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { useStyles } from './StandardDimensionsV2.styles'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { Product } from '../../../modules/productsListing/types'
import { ShortenedNameV2 } from '@labrav/common'
import { MultilineTruncate } from '../TruncateText/MultilineTruncate'

const EMPTY_STANDARD_DIMENSION_OBJECT = {
  label: '',
  labelDescription: '',
  type: 'standard' as DimensionType,
  currency: 'USD',
}

interface StandardDimensionsV2Props {
  formik: FormikProps<Record<string, unknown>>
  heading?: React.ReactNode
  subHeading?: React.ReactNode
  addButtonName: string
  productId: string
  isFlexible?: boolean
  hideNote?: boolean
  firstColumnName?: string
  isResale?: boolean
  product?: Product
}

export const StandardDimensionsV2 = ({
  formik,
  subHeading,
  heading,
  addButtonName,
  productId,
  isFlexible = false,
  hideNote = false,
  firstColumnName = 'Name',
  isResale,
  product: productFromProps,
}: StandardDimensionsV2Props) => {
  const { classes } = useStyles()
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = formik
  const dimensions = (values.dimensions || []) as Dimension[]
  const { products } = useFlyOutProductsBasedOnSelectedCloud()

  const product = useMemo(
    () =>
      productFromProps ||
      products.find(p => p.productId === productId) ||
      ({} as Product),
    [products, productId, productFromProps]
  )

  const standardDimensionsData = useMemo(
    () => dimensions.filter(d => d.type === 'standard'),
    [dimensions]
  )

  const dimensionsLength = standardDimensionsData.length

  const customDimensionLength = useMemo(
    () => dimensions.filter(d => d.type === 'custom').length,
    [dimensions]
  )

  const standardCustomDimensionLength = dimensionsLength + customDimensionLength

  const productDimensions = useMemo(() => {
    return (
      product?.productDimensions?.filter(
        dimension =>
          dimension.isAdditionalUsageAllowed === false ||
          isUndefined(dimension.isAdditionalUsageAllowed)
      ) || []
    )
  }, [product])

  const selectedDimensionNames = useMemo(
    () => standardDimensionsData.map(d => d.name),
    [standardDimensionsData]
  )

  const dimensionsOptions = useMemo(
    () =>
      productDimensions.map(dimension => ({
        label: dimension.label,
        value: dimension.name,
      })),
    [productDimensions]
  )

  const selectOptionsPerRowIndex = useMemo(() => {
    const result: Record<number, Array<{ label: string; value: string }>> = {}
    const names = selectedDimensionNames
    dimensions.forEach((dim, index) => {
      if (dim.type !== 'standard') return
      const otherSelected = new Set(names.filter(n => n !== dim.name))
      result[index] = dimensionsOptions.filter(
        opt => !otherSelected.has(opt.value)
      )
    })
    return result
  }, [dimensions, dimensionsOptions, selectedDimensionNames])

  const handleChangeSelect = useCallback(
    (event: SelectChangeEvent, index: number) => {
      const selectedLabel = event.target.value as string
      const {
        id,
        price,
        freeTrial,
        prices,
        valueDataType,
        ...selectedDimension
      } =
        productDimensions.find(dimension => dimension.name === selectedLabel) ||
        {}

      const updatedDimension = {
        ...selectedDimension,
        type: 'standard',
        dimensionId: id,
        quantity: '',
        price: '',
      }
      setFieldValue(`dimensions.${index}`, updatedDimension)
      setFieldTouched(`dimensions.${index}.quantity`, false)
    },
    [productDimensions, setFieldValue, setFieldTouched]
  )

  useEffect(() => {
    const nonAdditionalUsageDimension = dimensions.filter(
      dimension => dimension.type === 'standard' || dimension.type === 'custom'
    )
    if (nonAdditionalUsageDimension.length === 0) {
      setFieldValue('dimensions', [
        ...dimensions,
        { ...EMPTY_STANDARD_DIMENSION_OBJECT },
      ])
    }
  }, [dimensions, setFieldValue])

  return (
    <>
      {!(product.productType === PlanType.SAAS_SUBSCRIPTION) &&
        !hideNote &&
        !isResale && (
          <Typography className={classes.value}>
            Add at least one dimension from either the product dimension or
            custom dimension sections below.
            <Box
              sx={{
                mt: 2,
              }}
            >
              <NoteV2 description="You cannot create your private offer without adding atleast one dimension." />
            </Box>
          </Typography>
        )}
      {isResale && (
        <Typography className={classes.value}>
          <NoteV2
            description={
              <Typography className={classes.noteWrapper}>
                <Typography className={classes.note}>Note: </Typography>
                <Typography className={classes.noteText}>
                  You cannot create reseller authorization without adding
                  atleast one dimension.
                </Typography>
              </Typography>
            }
          />
        </Typography>
      )}
      {heading && (
        <Typography className={classes.heading}>{heading}</Typography>
      )}
      {subHeading && (
        <Typography className={classes.value}>{subHeading}</Typography>
      )}
      <div className={clsx(classes.root, classes.elementsGap)}>
        <div className={classes.titleWraper}>
          <Typography className={clsx(classes.sectionText, classes.columnName)}>
            {firstColumnName}
          </Typography>
          <Typography className={clsx(classes.sectionText, classes.columnDesc)}>
            Description
          </Typography>
          <Typography
            className={clsx(classes.sectionText, classes.columnPrice)}
          >
            {isFlexible ? 'Enter quantity' : 'Enter Price'}
          </Typography>
          <div style={{ width: '2%' }}></div>
        </div>
        <div className={classes.border}></div>
        <FieldArray name="dimensions">
          {({ push, remove }) => (
            <>
              {dimensions.map((dimension, index) => {
                const dimensionKey = dimension.id || index
                return dimension.type === 'standard' ? (
                  <Fragment key={dimensionKey}>
                    <div
                      className={classes.resourceRow}
                      data-testid="dimensions-row"
                    >
                      <div className={classes.resourcesContainer}>
                        <div
                          className={classes.columnName}
                          key={`dimensions-${
                            dimension.id || index
                          }-paymentDate`}
                        >
                          {!(
                            product.productType === PlanType.SAAS_SUBSCRIPTION
                          ) ? (
                            <div style={{ maxWidth: '240px' }}>
                              <SelectV4
                                field={{
                                  title: 'Select a dimension',
                                  placeHolder: 'Select a dimension',
                                  humanReadableKey: 'dimension',
                                  id: `dimensions.${[index]}.label`,
                                  isRequired: false,
                                  isReadOnly: false,
                                  isCustom: false,
                                  defaultValue: '',
                                  dataType: 'enum' as DataType,
                                  options:
                                    selectOptionsPerRowIndex[index] ?? [],
                                }}
                                value={dimensions[index].name || ''}
                                onChange={e => handleChangeSelect(e, index)}
                                onBlur={handleBlur}
                                isError={
                                  !!(
                                    get(
                                      touched,
                                      `dimensions.${[index]}.label`,
                                      false
                                    ) &&
                                    get(
                                      errors,
                                      `dimensions.${[index]}.label`,
                                      false
                                    )
                                  )
                                }
                                errorText={
                                  (!!get(
                                    touched,
                                    `dimensions.${[index]}.label`,
                                    false
                                  ) &&
                                    get(
                                      errors,
                                      `dimensions.${[index]}.label`,
                                      ''
                                    )) ||
                                  ''
                                }
                              />
                            </div>
                          ) : (
                            <div
                              className={clsx(
                                classes.alignDescription,
                                classes.descHeightWidth
                              )}
                              key={`dimensions-${dimensionKey}-name`}
                              data-testid="dimension-name"
                            >
                              <ShortenedNameV2
                                name={dimensions[index].name || '-'}
                                length={25}
                              />
                            </div>
                          )}
                        </div>
                        <div
                          className={clsx(
                            classes.alignDescription,
                            classes.descHeightWidth,
                            classes.wordWrap
                          )}
                          key={`dimensions-${dimensionKey}-desc`}
                          data-testid="description-select"
                        >
                          <LabraToolTipV2
                            tooltipPlacement={'right-end'}
                            description={
                              dimensions[index].labelDescription || '-'
                            }
                          >
                            <MultilineTruncate
                              text={dimensions[index].labelDescription || '-'}
                              maxLength={28}
                            />
                          </LabraToolTipV2>
                        </div>
                        {!isFlexible ? (
                          <div
                            key={`dimensions-${dimensionKey}-price`}
                            className={classes.columnPrice}
                          >
                            <TextInputV4
                              keyName={`dimensions.${[index]}.price`}
                              title="Price"
                              type="number"
                              placeHolder=""
                              value={
                                !isNil(dimensions[index]?.price)
                                  ? (dimensions[index]?.price as number)
                                  : ''
                              }
                              updatedValue={
                                !isNil(dimensions[index]?.price)
                                  ? (dimensions[index]?.price as number)
                                  : ''
                              }
                              isError={
                                !!(
                                  get(
                                    touched,
                                    `dimensions.${[index]}.price`,
                                    false
                                  ) &&
                                  get(
                                    errors,
                                    `dimensions.${[index]}.price`,
                                    false
                                  )
                                )
                              }
                              errorText={
                                (!!get(
                                  touched,
                                  `dimensions.${[index]}.price`,
                                  false
                                ) &&
                                  get(
                                    errors,
                                    `dimensions.${[index]}.price`,
                                    ''
                                  )) ||
                                ''
                              }
                              onChange={handleChange}
                              isReadOnly={
                                dimensions[index].dimensionId ? false : true
                              }
                              isRequired={false}
                              showSpinner={false}
                              onBlur={handleBlur}
                              keyToPrevent={['+', '-', 'e']}
                            />
                          </div>
                        ) : (
                          <div
                            key={`dimensions-${dimensionKey}-quantity`}
                            className={classes.columnPrice}
                          >
                            <TextInputV4
                              keyName={`dimensions.${[index]}.quantity`}
                              title="Quantity"
                              type="number"
                              placeHolder=""
                              value={
                                !isNil(dimensions[index]?.quantity)
                                  ? (dimensions[index]?.quantity as number)
                                  : ''
                              }
                              updatedValue={
                                !isNil(dimensions[index]?.quantity)
                                  ? (dimensions[index]?.quantity as number)
                                  : ''
                              }
                              isError={
                                !!(
                                  get(
                                    touched,
                                    `dimensions.${[index]}.quantity`,
                                    false
                                  ) &&
                                  get(
                                    errors,
                                    `dimensions.${[index]}.quantity`,
                                    false
                                  )
                                )
                              }
                              errorText={
                                (!!get(
                                  touched,
                                  `dimensions.${[index]}.quantity`,
                                  false
                                ) &&
                                  get(
                                    errors,
                                    `dimensions.${[index]}.quantity`,
                                    ''
                                  )) ||
                                ''
                              }
                              onChange={handleChange}
                              isReadOnly={
                                dimensions[index].dimensionId ? false : true
                              }
                              isRequired={false}
                              showSpinner={false}
                              onBlur={handleBlur}
                              keyToPrevent={['+', '-', 'e']}
                            />
                          </div>
                        )}
                      </div>
                      {standardCustomDimensionLength > 1 &&
                      !(product.productType === PlanType.SAAS_SUBSCRIPTION) ? (
                        <div className={classes.trashIcon}>
                          <FontAwesomeIcon
                            icon={faTrash}
                            style={{ width: '18px', height: '20px' }}
                            data-testid="trash-dimensions"
                            onClick={() => remove(index)}
                          />
                        </div>
                      ) : (
                        <div style={{ width: '2%' }}></div>
                      )}
                    </div>
                    <div className={classes.border}></div>
                  </Fragment>
                ) : (
                  <></>
                )
              })}
              {dimensionsLength <= productDimensions.length - 1 &&
              !(product.productType === PlanType.SAAS_SUBSCRIPTION) ? (
                <div>
                  <ButtonV2
                    className={classes.widthButton}
                    styleType="link"
                    onClick={() => {
                      push({ ...EMPTY_STANDARD_DIMENSION_OBJECT })
                    }}
                    data-testid="add-a-dimensions-button"
                    icon={
                      <FontAwesomeIcon
                        icon={faPlus}
                        style={{ width: '14px', height: '16px' }}
                      />
                    }
                    smallVariant
                  >
                    {addButtonName}
                  </ButtonV2>
                </div>
              ) : !(product.productType === PlanType.SAAS_SUBSCRIPTION) ? (
                <LabraToolTipV2
                  tooltipPlacement="bottom"
                  description={
                    'All existing dimensions have already been added.'
                  }
                  className={classes.widthButton}
                >
                  <ButtonV2
                    className={classes.widthButton}
                    styleType="link"
                    smallVariant
                    onClick={() => {
                      void 0
                    }}
                    data-testid="add-a-dimensions-button"
                    icon={
                      <FontAwesomeIcon
                        icon={faPlus}
                        style={{ width: '14px', height: '16px' }}
                      />
                    }
                    disabled={true}
                  >
                    {addButtonName}
                  </ButtonV2>
                </LabraToolTipV2>
              ) : (
                <></>
              )}
            </>
          )}
        </FieldArray>
      </div>
    </>
  )
}
